import { atom, useSetAtom } from "jotai";
import { outlineAtom } from "../atoms/outlineAtom";
import { potentialSystemsAtom } from "../atoms/potentialSystemsAtom";
import { streamedZslAtom } from "../atoms/streamedZslAtom";
import { streamNodesAndEdgesCommand } from "../atoms/streamNodesAndEdgesCommand";
import { fixAndParseJSON } from "../utils";

import { useStreamingCompletion } from "./useStreamingCompletion";

const setPartialOutlineAtom = atom(null, (get, set, partialOutline: string) => {
  const outline = get(outlineAtom);

  set(outlineAtom, outline + partialOutline);
  set(streamNodesAndEdgesCommand);
});

setPartialOutlineAtom.debugLabel = "setPartialOutlineAtom";

const setPotentialSystemsAtom = atom(null, (get, set, systems: string) => {
  const potentialSystems = get(potentialSystemsAtom);
  set(potentialSystemsAtom, potentialSystems + systems);
});

setPotentialSystemsAtom.debugLabel = "setPotentialSystemsAtom";

type Options = {
  api: string;
  onComplete: () => void;
  onError: (error: string) => void;
};

export function useZslPieceCompletion({ api, onComplete, onError }: Options) {
  const setZsl = useSetAtom(streamedZslAtom);
  const setPartialOutline = useSetAtom(setPartialOutlineAtom);
  const setFullOutline = useSetAtom(outlineAtom);
  const setPotentialSystems = useSetAtom(setPotentialSystemsAtom);

  const { completion, handleSubmit, triggerRequest } = useStreamingCompletion({
    api,
    onMessage(msg) {
      switch (msg.event) {
        case "potential-systems":
          setPotentialSystems(msg.data);
          break;
        case "partial-outline": {
          setPartialOutline(msg.data.slice(1, -1));
          break;
        }
        case "outline": {
          setFullOutline(msg.data);
          break;
        }
        // This is the case where the ZSL is returned
        case "decompressed": {
          setZsl(fixAndParseJSON(msg.data));
          break;
        }
        case "error":
          // throw an exception so it gets handled by the onerror callback below:
          // throw new Error(msg.data);
          onError(msg.data);
          break;
        default:
          break;
      }
    },
    onFinish() {
      onComplete();
    },
    onError(err) {
      // TODO: handle error
      console.error(err);
    },
  });

  return {
    completion,
    handleSubmit,
    triggerRequest,
  };
}
